import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Switch,
  Button,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Image,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSearch,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import { useDropzone } from "react-dropzone";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";

const VaccineManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [vaccines, setVaccines] = useState([]);
  const [filteredVaccines, setFilteredVaccines] = useState([]);
  const [livestockTypes, setLivestockTypes] = useState([]);
  const [vaccineTypes, setVaccineTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLivestockType, setFilterLivestockType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    VaccineID: "",
    VaccineNameTH: "",
    VaccineNameEN: "",
    LivestockType: "",
    VaccineType: "",
    Unit: "",
    PackageSize: "",
    CostPrice: "",
    PricePrevention: "",
    PriceDistribution: "",
    DiseaseType: "",
    UsageInstructions: "",
    Precautions: "",
    Remark: "",
    FileVaccineImage: null,
    FileVaccinePackage: null,
    FileVaccineLabel: null,
    FileOther: null,
    Status: "1",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadError, setUploadError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Check if user has permission to edit/add (OfficeType === 'สทช.')
  const hasEditPermission = profile?.OfficeType === "สทช.";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสวัคซีน",
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "150px",
    },
    {
      name: "ชื่อวัคซีน (TH)",
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: "ขนาดบรรจุ (โดส/ขวด)",
      selector: (row) => (row.PackageSize ? row.PackageSize.toString() : "-"),
      sortable: true,
      width: "200px",
    },
    {
      name: "ราคางบจำหน่าย (ขวด)",
      selector: (row) => (row.PriceDistribution ? row.PriceDistribution.toString() : "-"),
      sortable: true,
      width: "200px",
    },
    {
      name: "ราคางบป้องกัน (ขวด)",
      selector: (row) => (row.PricePrevention ? row.PricePrevention.toString() : "-"),
      sortable: true,
      width: "200px",
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
      width: "250px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) =>
        hasEditPermission ? (
          <IconButton.Edit
            icon={faEdit}
            onClick={() => handleEdit(row)}
            label="แก้ไข"
          />
        ) : (
          <IconButton.Edit
            icon={faEdit}
            onClick={() => handleEdit(row)}
            label="รายละเอียด"
          />
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchLivestockTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setLivestockTypes(response.LivestockTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  const fetchVaccineTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineTypes(response.VaccineTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching vaccine types:", error);
    }
  };

  const fetchVaccines = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccinesList || [];
        setVaccines(data);
        setFilteredVaccines(data);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchVaccines();
      fetchLivestockTypes();
      fetchVaccineTypes();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterLivestockType, filterStatus);
  };

  const handleLivestockTypeFilter = (e) => {
    const value = e.target.value;
    setFilterLivestockType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterLivestockType, value);
  };

  const applyFilters = (search, livestockType, status) => {
    let filtered = vaccines;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const livestockTypeName =
          livestockTypes.find((t) => t.LivestockTypeID === row.LivestockType)
            ?.LivestockTypeName ||
          row.LivestockType ||
          "";
        return searchTerms.every((term) =>
          [
            row.VaccineID.toLowerCase(),
            row.VaccineNameTH.toLowerCase(),
            row.VaccineNameEN.toLowerCase(),
            livestockTypeName.toLowerCase(),
            row.DiseaseType.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        );
      });
    }

    if (livestockType) {
      filtered = filtered.filter((row) => row.LivestockType === livestockType);
    }

    if (status) {
      filtered = filtered.filter((row) =>
        status === "1" ? row.Status : !row.Status
      );
    }

    setFilteredVaccines(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterLivestockType("");
    setFilterStatus("");
    setFilteredVaccines(vaccines);
  };

  const handleAddNew = () => {
    setFormData({
      VaccineID: "",
      VaccineNameTH: "",
      VaccineNameEN: "",
      LivestockType: "",
      VaccineType: "",
      Unit: "",
      PackageSize: "",
      CostPrice: "",
      PricePrevention: "",
      PriceDistribution: "",
      DiseaseType: "",
      UsageInstructions: "",
      Precautions: "",
      Remark: "",
      FileVaccineImage: null,
      FileVaccinePackage: null,
      FileVaccineLabel: null,
      FileOther: null,
      Status: "1",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
    setUploadError(null);
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      PackageSize: row.PackageSize ? row.PackageSize.toString() : "",
      CostPrice: row.CostPrice
        ? row.CostPrice.toString()
        : "",
      PricePrevention: row.PricePrevention
        ? row.PricePrevention.toString()
        : "",
      PriceDistribution: row.PriceDistribution
        ? row.PriceDistribution.toString()
        : "",
      FileVaccineImage: null,
      FileVaccinePackage: null,
      FileVaccineLabel: null,
      FileOther: null,
      Status: row.Status ? "1" : "0",
    });
    setIsEditing(true);
    setShowForm(true);
    setErrors({});
    setUploadError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleStatusChange = (e) => {
    setFormData({ ...formData, Status: e.target.checked ? "1" : "0" });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "VaccineID":
      case "VaccineNameTH":
      case "LivestockType":
      case "VaccineType":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "PackageSize":
        if (value && (isNaN(value) || value <= 0)) {
          newErrors[name] = "ขนาดบรรจุต้องเป็นตัวเลขมากกว่า 0";
        } else {
          delete newErrors[name];
        }
        break;
      case "CostPrice":
      case "PricePrevention":
      case "PriceDistribution":
        if (value && (isNaN(value) || value < 0)) {
          newErrors[name] = "ราคาต้องเป็นตัวเลขไม่ติดลบ";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.VaccineID &&
      formData.VaccineNameTH &&
      formData.LivestockType &&
      formData.VaccineType &&
      (!formData.PackageSize ||
        (!isNaN(formData.PackageSize) && formData.PackageSize > 0)) &&
      (!formData.CostPrice ||
        (!isNaN(formData.CostPrice) && formData.CostPrice >= 0)) &&
      (!formData.PricePrevention ||
        (!isNaN(formData.PricePrevention) && formData.PricePrevention >= 0)) &&
      (!formData.PriceDistribution ||
        (!isNaN(formData.PriceDistribution) &&
          formData.PriceDistribution >= 0)) &&
      Object.keys(errors).length === 0
    );
  };

  // Handle file preview
  const handleViewFile = (file) => {
    const fileData = {
      name: file.name,
      type: file.type,
      data: URL.createObjectURL(file), // Create a temporary URL for preview
    };
    setSelectedFile(fileData);
    setIsModalOpen(true);
  };

  // Dropzone components for file uploads
  const DropzoneField = ({ name, label }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
        "image/jpeg": [".jpeg", ".jpg"],
        "image/png": [".png"],
        "application/pdf": [".pdf"],
      },
      maxSize: 5 * 1024 * 1024, // 5MB limit
      onDrop: (acceptedFiles, fileRejections) => {
        if (!hasEditPermission) {
          setUploadError("คุณไม่มีสิทธิ์ในการอัปโหลดไฟล์");
          return;
        }
        if (acceptedFiles.length > 0) {
          setFormData({ ...formData, [name]: acceptedFiles[0] });
          setUploadError(null);
        }
        if (fileRejections.length > 0) {
          const errorMessage =
            fileRejections[0].errors[0].code === "file-too-large"
              ? "ไฟล์ต้องมีขนาดไม่เกิน 5MB"
              : "เฉพาะไฟล์ JPEG, PNG, และ PDF เท่านั้น";
          setUploadError(errorMessage);
        }
      },
      multiple: false,
      disabled: !hasEditPermission,
    });

    const handleRemoveFile = () => {
      if (!hasEditPermission) {
        setUploadError("คุณไม่มีสิทธิ์ในการลบไฟล์");
        return;
      }
      setFormData({ ...formData, [name]: null });
      setUploadError(null);
    };

    return (
      <FormControl isDisabled={!hasEditPermission}>
        <FormLabel>{label}</FormLabel>
        <Box
          {...getRootProps()}
          border="2px dashed"
          borderColor={isDragActive ? "blue.500" : "gray.300"}
          p={4}
          textAlign="center"
          borderRadius="md"
          bg={isDragActive ? "blue.50" : "white"}
          cursor={hasEditPermission ? "pointer" : "not-allowed"}
          opacity={hasEditPermission ? 1 : 0.6}
        >
          <input {...getInputProps()} />
          {formData[name] ? (
            <HStack justify="center">
              <Text
                color="blue.500"
                cursor={hasEditPermission ? "pointer" : "not-allowed"}
                onClick={() => hasEditPermission && handleViewFile(formData[name])}
              >
                {formData[name].name}
              </Text>
              {hasEditPermission && (
                <Button size="sm" colorScheme="red" onClick={handleRemoveFile}>
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
            </HStack>
          ) : (
            <Text>
              {hasEditPermission
                ? isDragActive
                  ? "วางไฟล์ที่นี่ ..."
                  : "ลากไฟล์มาวาง หรือคลิกเพื่อเลือกไฟล์ (JPEG, PNG, PDF, สูงสุด 5MB)"
                : "ไม่สามารถอัปโหลดไฟล์ได้"}
            </Text>
          )}
        </Box>
      </FormControl>
    );
  };

  const handleSave = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการบันทึกข้อมูล");
      return;
    }
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    setUploadError(null);
    try {
      const apiUrl = isEditing ? "/updateVaccinesByID" : "/createVaccines";
      const formDataToSend = new FormData();

      formDataToSend.append("VaccineID", formData.VaccineID || "");
      formDataToSend.append("VaccineNameTH", formData.VaccineNameTH || "");
      formDataToSend.append("VaccineNameEN", formData.VaccineNameEN || "");
      formDataToSend.append("LivestockType", formData.LivestockType || "");
      formDataToSend.append("VaccineType", formData.VaccineType || "");
      formDataToSend.append("Unit", formData.Unit || "");
      formDataToSend.append(
        "PackageSize",
        formData.PackageSize ? parseInt(formData.PackageSize) : ""
      );
      formDataToSend.append(
        "CostPrice",
        formData.CostPrice ? parseFloat(formData.CostPrice) : ""
      );
      formDataToSend.append(
        "PricePrevention",
        formData.PricePrevention ? parseFloat(formData.PricePrevention) : ""
      );
      formDataToSend.append(
        "PriceDistribution",
        formData.PriceDistribution ? parseFloat(formData.PriceDistribution) : ""
      );
      formDataToSend.append("DiseaseType", formData.DiseaseType || "");
      formDataToSend.append(
        "UsageInstructions",
        formData.UsageInstructions || ""
      );
      formDataToSend.append("Precautions", formData.Precautions || "");
      formDataToSend.append("Remark", formData.Remark || "");
      formDataToSend.append(
        "Status",
        formData.Status === "1" ? "true" : "false"
      );

      if (formData.FileVaccineImage) {
        formDataToSend.append("FileVaccineImage", formData.FileVaccineImage);
      }
      if (formData.FileVaccinePackage) {
        formDataToSend.append(
          "FileVaccinePackage",
          formData.FileVaccinePackage
        );
      }
      if (formData.FileVaccineLabel) {
        formDataToSend.append("FileVaccineLabel", formData.FileVaccineLabel);
      }
      if (formData.FileOther) {
        formDataToSend.append("FileOther", formData.FileOther);
      }

      console.log("formData before FormData:", formData);
      console.log("formDataToSend entries:");
      for (const [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value instanceof File ? value.name : value);
      }

      const apiHost =
        process.env.REACT_APP_API_URL || "http://192.168.1.122:3003";
      const apiKey =
        process.env.REACT_APP_API_KEY || "cb4b9a44fd024e9e24e1e6e6b2d26720";
      const headers = {
        "x-session-id": sessionId || "",
        apikey: apiKey,
      };

      console.log("Custom API Request:", {
        url: `${apiHost}${apiUrl}`,
        method: "POST",
        headers,
        body: "[FormData]",
      });

      const response = await fetch(`${apiHost}${apiUrl}`, {
        method: "POST",
        headers,
        body: formDataToSend,
      });

      const text = await response.text();
      console.log("Custom API URL:", apiUrl);
      console.log("Custom Raw Response:", text);

      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        throw new Error("Response is not valid JSON: " + text);
      }

      if (!response.ok) {
        throw new Error(
          result.error || `API request failed with status ${response.status}`
        );
      }

      if (result.result === "success") {
        alert(isEditing ? "อัปเดตวัคซีนสำเร็จ!" : "สร้างวัคซีนสำเร็จ!");
        setShowForm(false);
        fetchVaccines();
      } else {
        throw new Error(result.error || "API returned failure");
      }
    } catch (error) {
      console.error("Error saving vaccine:", error);
      alert(`เกิดข้อผิดพลาดในการบันทึก: ${error.message}`);
      setUploadError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      VaccineID: "",
      VaccineNameTH: "",
      VaccineNameEN: "",
      LivestockType: "",
      VaccineType: "",
      Unit: "",
      PackageSize: "",
      CostPrice: "",
      PricePrevention: "",
      PriceDistribution: "",
      DiseaseType: "",
      UsageInstructions: "",
      Precautions: "",
      Remark: "",
      FileVaccineImage: null,
      FileVaccinePackage: null,
      FileVaccineLabel: null,
      FileOther: null,
      Status: "1",
    });
    setErrors({});
    setUploadError(null);
  };

  const handleDelete = async () => {
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบวัคซีนนี้?")) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccinesByID",
        data: {
          ...formData,
          Status: false,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("ลบวัคซีนสำเร็จ!");
        setShowForm(false);
        fetchVaccines();
      }
    } catch (error) {
      console.error("Error deleting vaccine:", error);
      alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการข้อมูลวัคซีน
            </Heading>
          </HStack>
        </Box>

        {uploadError && (
          <Alert status="error">
            <AlertIcon />
            {uploadError}
          </Alert>
        )}

        {showForm && (
          <Box
            p={5}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="lg"
            w="100%"
            bg="white"
          >
            <Heading size="md" mb={4}>
              {isEditing ? "แก้ไขข้อมูลวัคซีน" : "เพิ่มข้อมูลวัคซีน"}
            </Heading>
            <Grid templateColumns={["1fr", "repeat(2, 1fr)", "repeat(4, 1fr)"]} gap={6}>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isRequired isInvalid={!!errors.VaccineID} isReadOnly={!hasEditPermission} isDisabled={isEditing && hasEditPermission}>
                  <FormLabel>รหัสวัคซีน</FormLabel>
                  <Input
                    name="VaccineID"
                    value={formData.VaccineID}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.VaccineID}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <FormControl isRequired isInvalid={!!errors.VaccineNameTH} isReadOnly={!hasEditPermission}>
                  <FormLabel>ชื่อวัคซีน (TH)</FormLabel>
                  <Input
                    name="VaccineNameTH"
                    value={formData.VaccineNameTH}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.VaccineNameTH}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ชื่อวัคซีน (EN)</FormLabel>
                  <Input
                    name="VaccineNameEN"
                    value={formData.VaccineNameEN}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isRequired isInvalid={!!errors.LivestockType} isDisabled={!hasEditPermission}>
                  <FormLabel>ประเภทปศุสัตว์</FormLabel>
                  <Select
                    name="LivestockType"
                    value={formData.LivestockType}
                    onChange={handleInputChange}
                    placeholder="เลือกประเภท"
                    colorScheme="iBlue"
                  >
                    {livestockTypes.map((type) => (
                      <option
                        key={type.LivestockTypeID}
                        value={type.LivestockTypeID}
                      >
                        {type.LivestockTypeName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.LivestockType}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isRequired isInvalid={!!errors.VaccineType} isDisabled={!hasEditPermission}>
                  <FormLabel>ประเภทวัคซีน</FormLabel>
                  <Select
                    name="VaccineType"
                    value={formData.VaccineType}
                    onChange={handleInputChange}
                    placeholder="เลือกประเภท"
                    colorScheme="iBlue"
                  >
                    {vaccineTypes.map((TypeID) => (
                      <option key={TypeID.TypeID} value={TypeID.TypeID}>
                        {TypeID.TypeName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.VaccineType}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>หน่วย</FormLabel>
                  <Input
                    name="Unit"
                    value={formData.Unit}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isInvalid={!!errors.PackageSize} isReadOnly={!hasEditPermission}>
                  <FormLabel>ขนาดบรรจุ (โดส/ขวด)</FormLabel>
                  <Input
                    name="PackageSize"
                    value={formData.PackageSize}
                    onChange={handleInputChange}
                    type="number"
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.PackageSize}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isInvalid={!!errors.CostPrice} isReadOnly={!hasEditPermission}>
                  <FormLabel>ราคาต้นทุน (บาท/ขวด)</FormLabel>
                  <Input
                    name="CostPrice"
                    value={formData.CostPrice}
                    onChange={handleInputChange}
                    type="number"
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.CostPrice}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isInvalid={!!errors.PricePrevention} isReadOnly={!hasEditPermission}>
                  <FormLabel>ราคางบป้องกัน (บาท/ขวด)</FormLabel>
                  <Input
                    name="PricePrevention"
                    value={formData.PricePrevention}
                    onChange={handleInputChange}
                    type="number"
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.PricePrevention}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 1, 1]}>
                <FormControl isInvalid={!!errors.PriceDistribution} isReadOnly={!hasEditPermission}>
                  <FormLabel>ราคางบจำหน่าย (บาท/ขวด)</FormLabel>
                  <Input
                    name="PriceDistribution"
                    value={formData.PriceDistribution}
                    onChange={handleInputChange}
                    type="number"
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>
                    {errors.PriceDistribution}
                  </FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 4, 4]}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ประเภทโรค</FormLabel>
                  <Input
                    name="DiseaseType"
                    value={formData.DiseaseType}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>วิธีใช้</FormLabel>
                  <Textarea
                    name="UsageInstructions"
                    value={formData.UsageInstructions}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>ข้อควรระวัง</FormLabel>
                  <Textarea
                    name="Precautions"
                    value={formData.Precautions}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <DropzoneField name="FileVaccineImage" label="ภาพวัคซีน" />
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <DropzoneField
                  name="FileVaccinePackage"
                  label="ภาพบรรจุภัณฑ์"
                />
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <DropzoneField name="FileVaccineLabel" label="ภาพฉลาก" />
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <DropzoneField name="FileOther" label="ไฟล์อื่นๆ" />
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>หมายเหตุ</FormLabel>
                  <Textarea
                    name="Remark"
                    value={formData.Remark}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={[1, 2, 2]}>
                <FormControl isDisabled={!hasEditPermission}>
                  <FormLabel>สถานะ</FormLabel>
                  <Switch
                    name="Status"
                    isChecked={formData.Status === "1"}
                    onChange={handleStatusChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
            </Grid>
            <HStack mt={6} justify="flex-end">
              <IconButton.Close onClick={handleCancel} label="ปิด" />
              {hasEditPermission && isEditing && (
                <IconButton.Delete onClick={handleDelete} label="ลบ" />
              )}
              {hasEditPermission && (
                <IconButton.Save onClick={handleSave} isLoading={isLoading} />
              )}
            </HStack>
          </Box>
        )}

        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup w={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาวัคซีน..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterLivestockType}
              onChange={handleLivestockTypeFilter}
              placeholder="เลือกประเภทปศุสัตว์"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {livestockTypes.map((type) => (
                <option key={type.LivestockTypeID} value={type.LivestockTypeID}>
                  {type.LivestockTypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear onClick={handleClearSearch} label="ล้าง" />
          </HStack>
          {hasEditPermission && (
            <IconButton.Add onClick={handleAddNew} label="เพิ่มข้อมูลใหม่" />
          )}
        </HStack>

        {isLoading ? (
          <Spinner size="md" />
        ) : vaccines.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลวัคซีน
          </Text>
        ) : filteredVaccines.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredVaccines}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>

      {/* Modal for viewing files */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ดูไฟล์: {selectedFile?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedFile?.type.startsWith("image/") ? (
              <Image src={selectedFile.data} alt={selectedFile.name} maxW="100%" />
            ) : selectedFile?.type === "application/pdf" ? (
              <Box>
                <Text>ไฟล์ PDF: {selectedFile.name}</Text>
                <Button
                  as="a"
                  href={selectedFile.data}
                  download={selectedFile.name}
                  colorScheme="blue"
                  mt={2}
                >
                  ดาวน์โหลด PDF
                </Button>
              </Box>
            ) : (
              <Text>ไม่สามารถแสดงไฟล์นี้ได้</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VaccineManagement;